import clsx from 'clsx';
import { PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import { ChevronDownIcon } from 'src/modules/common/icons/ChevronDownIcon';
import { ChevronUpIcon } from 'src/modules/common/icons/ChevronUpIcon';
import { CheckIcon } from 'src/modules/common/icons/CheckIcon';
import { useOutsideClick } from 'src/modules/common/hooks/useOutsideClick';

type Props<T> = PropsWithChildren<{
    selectedValue: T;
    renderOption?: (arg: T) => ReactNode;
    renderSelectedValue?: (arg: T) => ReactNode;
    options: Array<T>;
    size?: 'sm' | 'md' | 'lg';
    outlined?: boolean;
    shaded?: boolean;
    disabled?: boolean;
    hideCheckIcon?: boolean;
    isOptionDisabled?: (option: T) => boolean;
    onChange: (value: T) => void;
}>

export function DropdownField<T>({
    onChange,
    renderOption = (option: T) => String(option),
    renderSelectedValue,
    selectedValue,
    options,
    size = 'md',
    outlined = false,
    shaded = true,
    disabled,
    hideCheckIcon = false,
    isOptionDisabled = () => false,
    children,
}: Props<T>) {
    const renderTrigger = renderSelectedValue ?? renderOption;
    const [listOpened, setListOpened] = useState(false);

    const ref = useOutsideClick<HTMLDivElement>(() => setListOpened(false));

    const selected = options.find((option) => option === selectedValue) ?? null;

    const isOnlyOneOption = selected !== null && options.length === 1;

    const chevron = useMemo(() => {
        if (isOnlyOneOption) {
            return null;
        }

        return (
            <div className="vss-dropdown-field__value-icon">
                {listOpened
                    ? <ChevronUpIcon/>
                    : <ChevronDownIcon/>}
            </div>
        );
    }, [isOnlyOneOption, listOpened]);

    const handleSelect = (value: T) => {
        onChange(value);
        setListOpened(false);
    };

    const handleOpen = () => {
        if (isOnlyOneOption || disabled) {
            return null;
        }

        return setListOpened((prev) => !prev);
    };

    return (
        <div className={clsx({
            'vss-dropdown-field': true,
            'vss-dropdown-field--disabled': disabled,
        })}
        >
            {children && (
                <div className={clsx({
                    'vss-dropdown-field__label': true,
                    'vss-dropdown-field__label--disabled': disabled,
                })}
                >
                    {children}
                </div>
            )}

            <div className="vss-dropdown-field__container" ref={ref}>
                <button
                    type="button"
                    className={clsx({
                        'vss-dropdown-field__value': true,
                        'vss-dropdown-field__value--outlined': outlined,
                        'vss-dropdown-field__value--shaded': shaded,
                        'vss-dropdown-field__value--single': isOnlyOneOption,
                        'vss-dropdown-field__value--disabled': disabled,
                        [`vss-dropdown-field__value--${size}`]: true,
                    })}
                    onClick={handleOpen}
                    tabIndex={isOnlyOneOption || disabled ? -1 : 0}
                >
                    {selected !== null ? renderTrigger(selected) : null}

                    {chevron}
                </button>

                <div className={clsx({
                    'vss-dropdown-field__list': true,
                    'vss-dropdown-field__list--opened': listOpened,
                    'vss-dropdown-field__list--shaded': shaded,
                })}
                >
                    {options.map((option) => (
                        <button
                            key={String(option)}
                            type="button"
                            className={clsx({
                                'vss-dropdown-field__option': true,
                                'vss-dropdown-field__option--selected': selected === option,
                                'vss-dropdown-field__option--disabled': isOptionDisabled(option),
                            })}
                            disabled={isOptionDisabled(option)}
                            onClick={() => handleSelect(option)}
                        >
                            {renderOption(option)}

                            {selected === option && !hideCheckIcon && (
                                <span className="vss-dropdown-field__option-icon">
                                    <CheckIcon/>
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
